import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { workflowSetAppendDocumentField } from '~/actions/workflow';
import { customSettingsKeys } from '~/common/constants/workflow';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';

import { FieldSelect } from '../DataFieldsMapping/FieldSelect';
import Section from '../Section';

type Field = {
  id: string;
  name: string;
  type: string;
  customSettings?: Record<string, any>;
};

type State = {
  workflow: {
    fields: Record<string, Field>;
  };
};

interface Props {
  fields: Record<string, Field>;
  appendDocumentFieldId: string | null;
  workflowSetAppendDocumentField: (id: string | null) => void;
}

function AppendDocument({
  fields,
  appendDocumentFieldId,
  workflowSetAppendDocumentField,
}: Props) {
  const intl = useIntl();
  const FILE_LIMIT_KEY = customSettingsKeys[WorkflowFieldType.File].FILE_LIMIT;

  const attachmentFields = Object.values(fields)
    .filter(
      (field) =>
        field.type === WorkflowFieldType.File &&
        field.customSettings?.[FILE_LIMIT_KEY] === 'single',
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const fieldOptions = attachmentFields.map((field) => ({
    label: field.name,
    value: field.id,
  }));

  const allOptions = [{ label: '', value: '' }, ...fieldOptions];

  return (
    <Section
      title={intl.formatMessage({
        id: 'AppendDocument.sectionTitle',
        defaultMessage: 'Append a Document to the Contract',
        description: 'Title for append document section',
      })}
      icon="file"
      id="workflow--settings-append-document-header"
      description={
        <FormattedMessage
          id="AppendDocument.sectionDescription"
          defaultMessage="When chosen, one attachment will be appended to the contract when leaving the Edit stage. The combined document will be sent to the e-signature platform and will appear in the post-sig repository when the ticket is completed."
          description="Description for append document section"
        />
      }
    >
      <Layout mt={2} align="center" spacing={4}>
        <FieldSelect
          filteredValues={[]}
          id="append-document-field-select"
          name="Append Document Field"
          options={allOptions}
          placeholder={intl.formatMessage({
            id: 'AppendDocument.noAttachmentFieldPlaceholder',
            defaultMessage: 'No attachment field chosen',
            description: 'Placeholder when no attachment field is chosen',
          })}
          shouldValidate={false}
          value={appendDocumentFieldId}
          onChange={workflowSetAppendDocumentField}
        />
      </Layout>
    </Section>
  );
}

const mapStateToProps = (state: State) => {
  const IS_DOCUMENT_PART_KEY =
    customSettingsKeys[WorkflowFieldType.File].IS_DOCUMENT_PART;

  const fields = state.workflow.fields;

  const selectedField = Object.values(fields).find(
    (field) =>
      field.type === WorkflowFieldType.File &&
      field.customSettings?.[IS_DOCUMENT_PART_KEY] === true,
  );

  return {
    fields,
    appendDocumentFieldId: selectedField?.id ?? null,
  };
};

const mapDispatchToProps = {
  workflowSetAppendDocumentField,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppendDocument);
